/**
 * BigwaStatusPanel — Painel unificado de feedback no bottom-left
 *
 * Fusão de ImportProgressPanel + NotificationPanel.
 * Um único canal de feedback pós-decisão.
 *
 * Estrutura:
 * ┌──────────────────────┐
 * │ Queue content        │ ← importQueueStore (janela única, 4 fases)
 * │ — OU —               │
 * │ Task progress        │ ← import/export progress
 * ├──────────────────────┤
 * │ Notification header  │ ← items acumuláveis
 * │ Notification items   │
 * └──────────────────────┘
 *
 * Queue tem prioridade sobre task progress.
 * Tasks e items podem coexistir.
 */

import React from 'react';
import { useShallow } from 'zustand/shallow';
import { useBigwaStatusPanelStore } from '../../stores/bigwaStatusPanelStore';
import { useImportQueueStore } from '../../stores/importQueueStore';
import type { QueuedFile } from '../../stores/importQueueStore';
import { kindLabel } from '../../services/pdfKindHeuristic';
import { CloseIcon } from '../bigwa-icons';
import { StatusPanelItem } from './StatusPanelItem';
import { ProgressSection } from './ProgressSection';
import { ChevronUp, ChevronDown, X, CheckCircle2, AlertCircle, Info } from 'lucide-react';
import './BigwaStatusPanel.css';

const DEFAULT_TASK_ID = '__default__';

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

export function BigwaStatusPanel() {
  const hasTask = useBigwaStatusPanelStore(s => s.tasks.some(t => t.id === DEFAULT_TASK_ID));
  const hasItems = useBigwaStatusPanelStore(s => s.items.length > 0);
  const queuePhase = useImportQueueStore(s => s.phase);

  const showQueue = queuePhase !== 'idle';
  const showTask = !showQueue && hasTask;
  const showNotifications = hasItems;
  const showPanel = showQueue || showTask || showNotifications;

  if (!showPanel) return null;

  // Queue em desambiguação: painel mais largo
  const isDisambiguating = queuePhase === 'disambiguating';
  const panelWidth = isDisambiguating ? 360 : 340;
  const hasTaskContent = showQueue || showTask;

  return (
    <div
      className="bsp"
      style={{
        width: panelWidth,
        minHeight: hasTaskContent ? 140 : undefined,
      }}
    >
      {/* Task/Queue section */}
      {showQueue && <QueueContent />}
      {showTask && <ProgressSection />}

      {/* Separator if both sections are visible */}
      {hasTaskContent && showNotifications && <div className="bsp__separator" />}

      {/* Notification section */}
      {showNotifications && <NotificationSection />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// NOTIFICATION SECTION
// ═══════════════════════════════════════════════════════════════

function NotificationSection() {
  const { items, isCollapsed, headerTitle } = useBigwaStatusPanelStore(
    useShallow(s => ({ items: s.items, isCollapsed: s.isCollapsed, headerTitle: s.headerTitle }))
  );
  const toggleCollapsed = useBigwaStatusPanelStore(s => s.toggleCollapsed);
  const dismissItems = useBigwaStatusPanelStore(s => s.dismissItems);
  const pauseTimer = useBigwaStatusPanelStore(s => s.pauseTimer);
  const resumeTimer = useBigwaStatusPanelStore(s => s.resumeTimer);

  const title = headerTitle || generateTitle(items);

  if (items.length === 0) return null;

  return (
    <div onMouseEnter={pauseTimer} onMouseLeave={resumeTimer}>
      {/* Header */}
      <div className="bsp__notif-header">
        <HeaderIcon items={items} />
        <span className="bsp__notif-title">{title}</span>
        <div className="bsp__notif-header-actions">
          <button
            className="bsp__header-btn"
            onClick={toggleCollapsed}
            title={isCollapsed ? 'Expandir' : 'Recolher'}
          >
            {isCollapsed ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          <button
            className="bsp__header-btn"
            onClick={dismissItems}
            title="Fechar"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className={`bsp__notif-body ${isCollapsed ? 'bsp__notif-body--collapsed' : ''}`}>
        {items.map(item => (
          <StatusPanelItem key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
}

// ─── Header icon ─────────────────────────────────────────────

function HeaderIcon({ items }: { items: Array<{ status: string }> }) {
  const hasError = items.some(i => i.status === 'error');
  const hasPending = items.some(i => i.status === 'pending');

  if (hasError) return <AlertCircle size={18} color="#D4918A" className="bsp__notif-header-icon" />;
  if (hasPending) return <Info size={18} color="#F2BC41" className="bsp__notif-header-icon" />;
  return <CheckCircle2 size={18} color="#477B72" className="bsp__notif-header-icon" />;
}

// ─── Title generator ─────────────────────────────────────────

function generateTitle(items: Array<{ status: string }>): string {
  const count = items.length;
  if (count === 0) return '';

  const errorCount = items.filter(i => i.status === 'error').length;
  const pendingCount = items.filter(i => i.status === 'pending').length;

  if (pendingCount > 0) {
    return pendingCount === 1 ? '1 operação em andamento' : `${pendingCount} operações em andamento`;
  }
  if (errorCount > 0 && errorCount === count) {
    return errorCount === 1 ? '1 erro' : `${errorCount} erros`;
  }
  if (count === 1) {
    return items[0].status === 'error' ? '1 erro' : '1 ação concluída';
  }
  if (errorCount > 0) {
    return `${count - errorCount} concluída${count - errorCount > 1 ? 's' : ''}, ${errorCount} erro${errorCount > 1 ? 's' : ''}`;
  }
  return `${count} ${count === 1 ? 'ação concluída' : 'ações concluídas'}`;
}

// ═══════════════════════════════════════════════════════════════
// QUEUE CONTENT — Janela Única (4 fases visuais)
// ═══════════════════════════════════════════════════════════════

function QueueContent() {
  const phase = useImportQueueStore(s => s.phase);
  const files = useImportQueueStore(s => s.files);

  if (phase === 'analyzing') return <QueueAnalyzing files={files} />;
  if (phase === 'disambiguating') return <QueueDisambiguating files={files} />;
  if (phase === 'processing') return <QueueProcessing files={files} />;
  if (phase === 'complete') return <QueueComplete files={files} />;
  return null;
}

// ─── Analyzing ───────────────────────────────────────────────

function QueueAnalyzing({ files }: { files: QueuedFile[] }) {
  const cancel = useImportQueueStore(s => s.cancel);
  const analyzing = files.filter(f => f.routeStatus === 'analyzing').length;
  const total = files.length;

  return (
    <>
      <QueueHeader title="Analisando arquivos" spinning cancel={cancel} />
      <div style={{ flex: 1, padding: '0 16px', overflowY: 'auto' }}>
        {files.map(f => <QueueFileRow key={f.id} file={f} />)}
      </div>
      <QueueFooter text={`Analisando ${analyzing} de ${total} arquivos`} cancel={cancel} />
    </>
  );
}

// ─── Disambiguating ──────────────────────────────────────────

function QueueDisambiguating({ files }: { files: QueuedFile[] }) {
  const cancel = useImportQueueStore(s => s.cancel);
  const resolveDisambiguation = useImportQueueStore(s => s.resolveDisambiguation);
  const resolveAllDisambiguations = useImportQueueStore(s => s.resolveAllDisambiguations);
  const pending = files.filter(f => f.routeStatus === 'needs-disambiguation');
  const resolved = files.filter(f => f.routeStatus === 'user-resolved').length;

  return (
    <>
      <QueueHeader title="Aguardando decisão" cancel={cancel} />
      <div style={{ flex: 1, padding: '0 16px', overflowY: 'auto' }}>
        {files.map(f => {
          if (f.routeStatus === 'needs-disambiguation') {
            // Tarefa 7.C — sugestão da heurística (sem decidir)
            const suggestsRepositorio = f.suggestedDestination === 'repositorio';
            const suggestsBiblioteca = f.suggestedDestination === 'biblioteca';
            return (
              <div key={f.id} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ color: '#F2BC41', fontSize: 14 }}>?</span>
                  <span style={{
                    fontSize: 13, color: '#d1d5db',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1,
                  }}>
                    {f.fileName}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 4, paddingLeft: 22 }}>
                  Sem DOI nem ISBN detectados. Onde guardar este arquivo?
                </div>
                {suggestsRepositorio && f.detectedKind && (
                  <div
                    style={{
                      fontSize: 11,
                      color: '#F2BC41',
                      marginBottom: 4,
                      paddingLeft: 22,
                      fontStyle: 'italic',
                      lineHeight: 1.4,
                    }}
                  >
                    Parece um {kindLabel(f.detectedKind)} — recomendamos enviar ao Repositório.
                  </div>
                )}
                <div style={{ fontSize: 10, color: '#6B7280', marginBottom: 2, paddingLeft: 22, lineHeight: 1.4 }}>
                  Biblioteca = livros, artigos, teses para CITAR
                </div>
                <div style={{ fontSize: 10, color: '#6B7280', marginBottom: 6, paddingLeft: 22, lineHeight: 1.4 }}>
                  Repositório = votos, prontuários, transcrições para ANALISAR
                </div>
                <div style={{ display: 'flex', gap: 8, paddingLeft: 22 }}>
                  <DisambiguateButton
                    label="Biblioteca"
                    onClick={() => resolveDisambiguation(f.id, 'biblioteca')}
                    title="Para livros, artigos, teses, capítulos a serem citados como referência no seu trabalho acadêmico"
                    highlighted={suggestsBiblioteca}
                  />
                  <DisambiguateButton
                    label="Repositório"
                    onClick={() => resolveDisambiguation(f.id, 'repositorio')}
                    title="Para votos, acórdãos, prontuários, transcrições, áudios, vídeos a serem analisados como dado empírico (Comparação, Conexões, etc.)"
                    highlighted={suggestsRepositorio}
                  />
                </div>
              </div>
            );
          }
          return <QueueFileRow key={f.id} file={f} />;
        })}
      </div>
      {pending.length >= 3 && (
        <div style={{ padding: '8px 16px', borderTop: '1px solid #2a2a2a', display: 'flex', gap: 8 }}>
          <DisambiguateButton
            label="Todos para Biblioteca"
            onClick={() => resolveAllDisambiguations('biblioteca')}
            title="Enviar todos os PDFs sem DOI para a Biblioteca (livros, artigos, teses)"
          />
          <DisambiguateButton
            label="Todos para Repositório"
            onClick={() => resolveAllDisambiguations('repositorio')}
            title="Enviar todos os PDFs sem DOI para o Repositório (votos, prontuários, transcrições, dado empírico)"
          />
        </div>
      )}
      <QueueFooter text={`Resolvendo ${resolved} de ${pending.length + resolved} pendências`} cancel={cancel} />
    </>
  );
}

function DisambiguateButton({
  label,
  onClick,
  title,
  highlighted,
}: {
  label: string;
  onClick: () => void;
  title?: string;
  highlighted?: boolean;
}) {
  const borderColor = highlighted ? '#477B72' : '#3a3a3a';
  const borderWidth = highlighted ? 2 : 1;
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        padding: '5px 12px', fontSize: 11, fontWeight: 500, borderRadius: 6,
        border: `${borderWidth}px solid ${borderColor}`, background: '#2a2a2a', color: '#d1d5db',
        cursor: 'pointer', transition: 'background 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = '#3a3a3a'; }}
      onMouseLeave={e => { e.currentTarget.style.background = '#2a2a2a'; }}
    >
      {label}
    </button>
  );
}

// ─── Processing ──────────────────────────────────────────────

function QueueProcessing({ files }: { files: QueuedFile[] }) {
  const processed = files.filter(f => f.processStatus === 'success' || f.processStatus === 'error').length;
  const total = files.length;
  const current = files.find(f => f.processStatus === 'processing');
  const progressPercent = total > 0 ? Math.round((processed / total) * 100) : 0;

  return (
    <>
      <QueueHeader title="Importando arquivos" spinning />
      <div style={{ flex: 1, padding: '0 16px', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
          <span style={{ fontSize: 12, color: '#9CA3AF' }}>Processando {processed} de {total}...</span>
          <span style={{ fontSize: 12, color: '#9CA3AF' }}>{progressPercent}%</span>
        </div>
        <div style={{ width: '100%', height: 3, backgroundColor: '#333', borderRadius: 2, overflow: 'hidden', marginBottom: 10 }}>
          <div style={{
            height: '100%', width: `${progressPercent}%`,
            backgroundColor: '#477B72', borderRadius: 2, transition: 'width 0.3s ease',
          }} />
        </div>
        {current && (
          <span style={{ fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {current.fileName}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <div style={{ borderTop: '1px solid #2a2a2a', padding: '10px 0', fontSize: 11, color: '#F2BC41' }}>
          Não feche a janela durante a importação
        </div>
      </div>
    </>
  );
}

// ─── Complete ────────────────────────────────────────────────

function QueueComplete({ files }: { files: QueuedFile[] }) {
  const reset = useImportQueueStore(s => s.reset);
  const bibliotecaCount = files.filter(f => f.route === 'biblioteca' && f.processStatus === 'success').length;
  const repositorioCount = files.filter(f => f.route === 'repositorio' && f.processStatus === 'success').length;
  const errors = files.filter(f => f.processStatus === 'error');
  // Tarefa 7.B — arquivos com confidence baixa precisam de revisão visual
  const lowConfidenceFiles = files.filter(f => f.lowConfidence && f.processStatus === 'success');
  const hasLowConfidence = lowConfidenceFiles.length > 0;
  const headerColor = hasLowConfidence ? '#F2BC41' : '#ffffff';
  const headerText = hasLowConfidence
    ? `Importação concluída — ${lowConfidenceFiles.length} ${lowConfidenceFiles.length === 1 ? 'precisa' : 'precisam'} de revisão`
    : 'Importação concluída';

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px 10px', flexShrink: 0 }}>
        <span style={{ fontSize: 14, fontWeight: 500, color: headerColor }}>{headerText}</span>
        <button onClick={reset} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', color: '#6b7280' }}>
          <CloseIcon size={14} variant="dark" />
        </button>
      </div>
      <div style={{ flex: 1, padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {bibliotecaCount > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#477B72', flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: '#d1d5db' }}>
              {bibliotecaCount} referência{bibliotecaCount !== 1 ? 's' : ''} importada{bibliotecaCount !== 1 ? 's' : ''}
            </span>
          </div>
        )}
        {repositorioCount > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#8B6914', flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: '#d1d5db' }}>
              {repositorioCount} dado{repositorioCount !== 1 ? 's' : ''} de pesquisa adicionado{repositorioCount !== 1 ? 's' : ''}
            </span>
          </div>
        )}
        {errors.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#D4918A', flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: '#d1d5db' }}>{errors.length} falha{errors.length !== 1 ? 's' : ''}</span>
          </div>
        )}
        {/* Tarefa 7.B — lista de arquivos com confidence baixa */}
        {hasLowConfidence && (
          <div style={{ borderTop: '1px solid #2a2a2a', marginTop: 4, paddingTop: 6, overflow: 'auto', maxHeight: 80 }}>
            <div style={{ fontSize: 10, color: '#F2BC41', marginBottom: 4, fontWeight: 500 }}>
              Confiança baixa — revise os metadados:
            </div>
            {lowConfidenceFiles.map((f) => (
              <div
                key={f.id}
                style={{
                  fontSize: 11,
                  color: '#F2BC41',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  paddingLeft: 8,
                }}
                title={`${f.fileName} — metadata extraída tem baixa confiança. Revise os campos antes de citar.`}
              >
                {f.fileName}
              </div>
            ))}
          </div>
        )}
        <div style={{ flex: 1 }} />
        {errors.length === 0 && !hasLowConfidence && (
          <div style={{ borderTop: '1px solid #2a2a2a', padding: '10px 0', fontSize: 11, color: '#F2BC41' }}>
            Todos processados sem problemas.
          </div>
        )}
        {errors.length > 0 && (
          <div style={{ borderTop: '1px solid #2a2a2a', padding: '8px 0', overflow: 'auto', maxHeight: 60 }}>
            {errors.map((f) => (
              <div key={f.id} style={{ fontSize: 11, color: '#D4918A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {f.fileName}: {f.error || 'Erro desconhecido'}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// SHARED QUEUE SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════

function QueueHeader({ title, spinning, cancel }: { title: string; spinning?: boolean; cancel?: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px 10px', flexShrink: 0 }}>
      <span style={{ fontSize: 14, fontWeight: 500, color: '#ffffff' }}>{title}</span>
      {spinning ? <Spinner /> : cancel ? (
        <button onClick={cancel} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', color: '#6b7280' }}>
          <CloseIcon size={14} variant="dark" />
        </button>
      ) : null}
    </div>
  );
}

function QueueFooter({ text, cancel }: { text: string; cancel: () => void }) {
  return (
    <div style={{ borderTop: '1px solid #2a2a2a', padding: '8px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
      <span style={{ fontSize: 11, color: '#9CA3AF' }}>{text}</span>
      <button
        onClick={cancel}
        title="Cancelar importação"
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', color: '#6b7280' }}
      >
        <CloseIcon size={12} variant="dark" />
      </button>
    </div>
  );
}

const ROUTE_LABELS: Record<string, string> = {
  biblioteca: 'Biblioteca',
  repositorio: 'Repositório',
};

const QueueFileRow = React.memo(function QueueFileRow({ file }: { file: QueuedFile }) {
  const routeLabel = file.route ? `→ ${ROUTE_LABELS[file.route]}` : '';
  const statusColor = file.routeStatus === 'auto-routed' || file.routeStatus === 'user-resolved'
    ? '#477B72'
    : file.routeStatus === 'analyzing'
      ? '#F2BC41'
      : '#9CA3AF';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: statusColor, flexShrink: 0 }} />
      <span style={{
        fontSize: 12, color: '#d1d5db', flex: 1,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {file.fileName}
      </span>
      {file.routeStatus === 'analyzing' && (
        <span style={{ fontSize: 11, color: '#F2BC41' }}>analisando...</span>
      )}
      {(file.routeStatus === 'auto-routed' || file.routeStatus === 'user-resolved') && (
        <span style={{ fontSize: 11, color: '#9CA3AF' }}>{routeLabel}</span>
      )}
    </div>
  );
});

function Spinner() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" style={{ animation: 'bsp-spin 1s linear infinite', flexShrink: 0 }}>
      <circle cx="8" cy="8" r="6" fill="none" stroke="#477B72" strokeWidth="2" strokeDasharray="28" strokeDashoffset="8" strokeLinecap="round" />
    </svg>
  );
}
